import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import { getSearchParameter } from "components/Shared/Functions";
import { MCard, MGrid, MSpinner, MSplitter, MDialog, ActionButtons } from "components/Shared";
import { ACTION_ADD } from "components/Shared/MGrid.tsx";
import { addCourse, searchCourses, updateCourse } from "requests/course";
import { DATA_TYPE_DATE_TIME, DATA_TYPE_DROPDOWN2, DATA_TYPE_NUMBER } from "typeConstants/dataTypes";
import CourseRoles from "components/Courses/CourseRoles";
import CourseEmployees from "components/Courses/CourseEmployees";
import { Grid, Cell, Button } from "react-md";
import { userHasRight } from "selectors/rights";
import { EMPLOYEE_ROLE_COURSE_VIEW, EMPLOYEE_COURSE_VIEW } from "typeConstants/rights";
import { getGroups } from "requests/courseGroup";
import { updateHeader, clearHeader } from "actions/layout";
import { getStates} from 'actions/region';

const getLocalISOTime = () => {
  let localDate = new Date();
  let tzoffset = localDate.getTimezoneOffset() * 60000;
  return (new Date(localDate - tzoffset)).toISOString().slice(0, -1);
};

const ManageCourses = ({
    location,
    canViewEmployeeRoleCourses,
    canViewEmployeeCourses,
    history,
    updateHeader,
    clearHeader,
    getStates,
    stateList,
  }) => {
  const [courses, setCourses] = useState();
  const [courseGroups, setCourseGroups] = useState();
  const [selectedCourse, setSelectedCourse] = useState();
  const [selectedGridIndex, setSelectedGridIndex] = useState();
  const [isMakingNewCourse, setIsMakingNewCourse] = useState(false);
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [gridLastLoadedAt, setGridLastLoadedAt] = useState(new Date());

  const mounted = useRef(true);

  const getCourseList = (highlightId) => {
    searchCourses(getSearchParameter(location) ?? "", true, (courses) => {
      // sort disabled courses last
      courses.sort((c1, c2) => {
        if (c1.disabledDate === c2.disabledDate) return c1.id - c2.id;
        else return (!!c1.disabledDate - !!c2.disabledDate);
      });

      setCourses(courses);
      setGridLastLoadedAt(new Date());

      // check if they selected a course to highlight
      if (highlightId != null) {
        const index = courses.findIndex(c => c.id === highlightId);
        if (index >= 0) {
          setSelectedGridIndex(index);
          setSelectedCourse(courses[index]);
        }
      }
    }, mounted);
  };

  useEffect(() => {
    getStates();
    getCourseList();
    getGroups(setCourseGroups, mounted);
    updateHeader({
      buttons: [{
        name: "Manage Course Categories",
        onClick: () => history.push("/manage-course-groups"),
      }]
    });
    return () => {
      mounted.current = false;
      clearHeader();
    };
  }, []);

  const onCourseSelected = index => {
    setSelectedCourse(courses[index]);
    setSelectedGridIndex(index);
    setIsMakingNewCourse(false);
  };

  const onCreateCourse = () => {
    setSelectedCourse({
      id: null,
      code: "",
      name: "",
      url: "",
      frequencyMonths: 12,
      expectedMinutes: null,
      createdDate: `${new Date()}`,
      disabledDate: null,
    });
    setSelectedGridIndex(null);
    setIsMakingNewCourse(true);
  };

  const updateSelectedCourse = newCourse => updateCourse(newCourse, () => getCourseList(), mounted);

  const onDisableClicked = areYouSure => {
    if (areYouSure) {
      let newSelectedCourse = { ...selectedCourse }; // copy so we arent mutating state
      if (newSelectedCourse.disabledDate) newSelectedCourse.disabledDate = null;
      else newSelectedCourse.disabledDate = getLocalISOTime(); // cast to string so we dont have timezone conversion issues

      let postCourse = {
        id: newSelectedCourse.id,
        name: newSelectedCourse.name,
        url: newSelectedCourse.url,
        frequencyMonths: newSelectedCourse.frequencyMonths,
        expectedMinutes: newSelectedCourse.expectedMinutes,
        disabledDate: newSelectedCourse.disabledDate,
        courseGroupId: newSelectedCourse.courseGroup?.id,
        stateAbbreviation: newSelectedCourse.stateAbbreviation,
      };

      updateSelectedCourse(postCourse);
      setSelectedCourse(newSelectedCourse);
      setShowDisableDialog(false);
    } else {
      setShowDisableDialog(true);
    }
  };

  const onEditCancelled = () => {
    if (isMakingNewCourse) {
      setIsMakingNewCourse(false);
      setSelectedCourse(null);
      setSelectedGridIndex(null);
    }
  };

  const onEditFinished = (_, editedCourse) => {
    const getField = field => editedCourse[field] ?? selectedCourse?.[field];

    let newCourse = {
      id: selectedCourse?.id ?? null,
      name: getField("name"),
      url: getField("url"),
      frequencyMonths: getField("frequencyMonths"),
      expectedMinutes: getField("expectedMinutes"),
      disabledDate: getField("disabledDate"),
      courseGroupId: editedCourse.courseGroupId ?? selectedCourse?.courseGroup?.id,
      stateAbbreviation: editedCourse.stateAbbreviation ?? getField("stateAbbreviation"),
    };

    if (isMakingNewCourse) {
      addCourse(newCourse, (id) => {
        setIsMakingNewCourse(false);
        getCourseList(id);
      }, mounted);
    } else {
      // editing an existing course
      setSelectedCourse(newCourse);
      updateSelectedCourse(newCourse);
    }
  };

  let grid = (<MGrid
      key={`course-grid-${gridLastLoadedAt.getTime()}`}
      headerText="Courses"
      actions={[{ code: ACTION_ADD, onClick: onCreateCourse, tooltip: "Create New Course" }]}
      data={courses}
      onRowClick={i => onCourseSelected(i)}
      rowLastClicked={selectedGridIndex}
      columnDefinitions={[
        { name: "Id", width: 75, dataSource: "id" },
        { name: "Code", width: 75, dataSource: "code" },
        { name: "Name", width: 180, dataSource: "name" },
        { name: "Category", width: 180, dataSource: "courseGroup.name" },
        { name: "URL", dataSource: "url" },
        { name: "Frequency (Months)", width: 150, dataSource: "frequencyMonths" },
        { name: "Expected Minutes", width: 150, dataSource: "expectedMinutes" },
        { name: "Create Date", width: 150, dataSource: "createdDate", dataType: DATA_TYPE_DATE_TIME },
        { name: "Disabled Date", width: 150, dataSource: "disabledDate", dataType: DATA_TYPE_DATE_TIME },
        { name: "State", width: 150, dataSource: "state.abbreviation" },
      ].map(d => ({ key: d.dataSource, ...d }))}
    />);

  let secondPane;

  if (selectedCourse) {
    let cardFields = [
      { key: "id", label: "Id", value: selectedCourse.id, editable: false },
      { key: "code", label: "Code", value: selectedCourse.code, editable: false },
      { key: "name", label: "Name", value: selectedCourse.name },
      {
        key: "courseGroupId",
        label: "Category",
        value: { label: selectedCourse.courseGroup?.name, value: selectedCourse.courseGroup?.id },
        dataType: DATA_TYPE_DROPDOWN2,
        dataTypeOptions: { options: courseGroups.map(g => ({ label: g.name, value: g.id })) }
      },
      { key: "url", label: "URL", value: selectedCourse.url },
      { key: "frequencyMonths", label: "Frequency (Months)", value: selectedCourse.frequencyMonths, dataType: DATA_TYPE_NUMBER },
      { key: "expectedMinutes", label: "ExpectedMinutes", value: selectedCourse.expectedMinutes, dataType: DATA_TYPE_NUMBER },
      { key: "createdDate", label: "Create Date", value: selectedCourse.createdDate, dataType: DATA_TYPE_DATE_TIME, editable: false },
      { key: "disabledDate", label: "Disabled Date", value: selectedCourse.disabledDate, dataType: DATA_TYPE_DATE_TIME, editable: false },
      {
        key: "stateAbbreviation",
        label: "State",
        value: { label: selectedCourse.state?.abbreviationAndName, value: selectedCourse.state?.stateAbbreviation },
        dataType: DATA_TYPE_DROPDOWN2,
        dataTypeOptions: { options: stateList.map(s => ({ label: s.abbreviationAndName, value: s.abbreviation })), nullable: true },
      }
    ];
    if (!selectedCourse.disabledDate && !isMakingNewCourse) cardFields.push({
      key: "disableButton",
      control: <Button primary raised onClick={() => onDisableClicked(false)}>Disable</Button>,
      editFields: [],
    });

    let shouldShowCourseRoles =
        canViewEmployeeRoleCourses &&
        !isMakingNewCourse &&
        !selectedCourse.disabledDate &&
        selectedCourse?.id;

    let shouldShowCourseEmployees =
        canViewEmployeeCourses &&
        !isMakingNewCourse &&
        !selectedCourse.disabledDate &&
        selectedCourse?.id;

    //change the key to the card if we're making a new course so the startInEditMode prop works
    secondPane = (<>
      <Grid className="md-grid-cards">
        <Cell size={12}>
          <MCard
            key={isMakingNewCourse ? "new" : "current"}
            title="Course Details"
            isEditable
            startInEditMode={isMakingNewCourse}
            onEditFinished={onEditFinished}
            onEditCancelled={onEditCancelled}
            fields={cardFields}
          />
        </Cell>
        <Cell size={12}>
          {shouldShowCourseRoles && selectedCourse.id && <CourseRoles courseId={selectedCourse.id} />}
        </Cell>
        <Cell size={12}>
          {shouldShowCourseEmployees && selectedCourse.id && <CourseEmployees courseId={selectedCourse.id} />}
        </Cell>
      </Grid>
    </>);
  }

  return courses ? <>
    <MSplitter
      firstChild={grid}
      secondChildTitle={{
        title: isMakingNewCourse ? "New Course" : "Course Details",
        buttons: [{ icon: "close", onClick: () => { setSelectedCourse(null); setSelectedGridIndex(null); } }]
      }}
      secondChild={secondPane}
    />
    <MDialog
      visible={showDisableDialog}
      width={350}
    >
      Are you sure you want to disable this course?
      <ActionButtons buttonPropSets={[
        { children: "Yes", onClick: () => onDisableClicked(true) },
        { children: "No", onClick: () => setShowDisableDialog(false) },
      ]} />
    </MDialog>
    </> :
    <MSpinner />;
};

ManageCourses.propTypes = {
  location: PropTypes.object,
  history: PropTypes.object,
  updateHeader: PropTypes.func,
  clearHeader: PropTypes.func,
  canViewEmployeeRoleCourses: PropTypes.bool.isRequired,
  canViewEmployeeCourses: PropTypes.bool.isRequired,
  stateList: PropTypes.array,
  getStates: PropTypes.func,
};

const mapStateToProps = state => ({
  canViewEmployeeRoleCourses: userHasRight(EMPLOYEE_ROLE_COURSE_VIEW)(state),
  canViewEmployeeCourses: userHasRight(EMPLOYEE_COURSE_VIEW)(state),
  stateList: state.region.states,
});

const mapDispatchToProps = dispatch => ({
  updateHeader(headerDefinitions) {
    dispatch(updateHeader(headerDefinitions));
  },
  clearHeader() {
    dispatch(clearHeader());
  },
  getStates() {
    dispatch(getStates());
  },
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ManageCourses));
